import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Select, Space, Table, notification } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import { adminGrantCompany, adminGrantCompanyAll } from '../../api/admin';
import { levelOptions } from './accessLevelHelp';

type UserCompanyAccessModalProps = {
  open: boolean;
  onCancel: () => void;
  user?: any | null;
  permissions?: any | null;
  onChanged?: (uid?: string) => void;
};

const UserCompanyAccessModal: React.FC<UserCompanyAccessModalProps> = ({
  open,
  onCancel,
  user,
  permissions,
  onChanged,
}) => {
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  const [loadingIds, setLoadingIds] = useState<Record<string, boolean>>({});
  const [loadingWildcard, setLoadingWildcard] = useState(false);

  const accessMap = permissions?.companyAccess || {};
  const wildcardLevel = accessMap['*'];
  const entries = useMemo(() => {
    return Object.keys(accessMap)
      .filter(id => id !== '*')
      .map(id => ({ id, level: accessMap[id] }));
  }, [accessMap]);

  useEffect(() => {
    if (!open || entries.length === 0) return;
    const missingIds = entries.map(e => e.id).filter(id => !(id in nameMap));
    if (missingIds.length === 0) return;

    const loadNames = async () => {
      const next = { ...nameMap };
      const chunkSize = 10;
      for (let i = 0; i < missingIds.length; i += chunkSize) {
        const chunk = missingIds.slice(i, i + chunkSize);
        const q = query(collection(db, 'companies'), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        snap.docs.forEach(doc => {
          const data = doc.data() as any;
          next[doc.id] = data?.name || doc.id;
        });
        chunk.forEach(id => {
          if (!next[id]) next[id] = id;
        });
      }
      setNameMap(next);
    };

    loadNames().catch((e) => {
      notification.error({
        message: 'Failed to load companies',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    });
  }, [entries, nameMap, open]);

  const handleUpdate = async (companyId: string, level: number) => {
    try {
      setLoadingIds(prev => ({ ...prev, [companyId]: true }));
      await adminGrantCompany({ uid: user?.id, companyId, level });
      notification.success({
        message: 'Company access updated',
        placement: 'topRight',
      });
      onChanged?.(user?.id);
    } catch (e: any) {
      notification.error({
        message: 'Update failed',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    } finally {
      setLoadingIds(prev => ({ ...prev, [companyId]: false }));
    }
  };

  const handleRemove = async (companyId: string) => {
    await handleUpdate(companyId, 0);
  };

  const handleWildcardUpdate = async (level: number) => {
    try {
      setLoadingWildcard(true);
      await adminGrantCompanyAll({ uid: user?.id, level });
      notification.success({
        message: 'Wildcard company access updated',
        placement: 'topRight',
      });
      onChanged?.(user?.id);
    } catch (e: any) {
      notification.error({
        message: 'Update failed',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    } finally {
      setLoadingWildcard(false);
    }
  };

  const columns = [
    {
      title: 'Company',
      dataIndex: 'id',
      render: (id: string) => nameMap[id] || id,
    },
    {
      title: 'Level',
      dataIndex: 'level',
      render: (level: number, row: any) => (
        <Select
          value={level}
          options={levelOptions}
          style={{ width: 160 }}
          onChange={(v) => handleUpdate(row.id, v)}
        />
      ),
    },
    {
      title: 'Actions',
      render: (_: any, row: any) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          loading={!!loadingIds[row.id]}
          onClick={() => handleRemove(row.id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title={`Company Access${user?.email ? ` — ${user.email}` : ''}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={720}
    >
      {wildcardLevel != null && (
        <Space style={{ marginBottom: 16 }}>
          <strong>All Companies</strong>
          <Select
            value={wildcardLevel}
            options={levelOptions}
            style={{ width: 160 }}
            onChange={handleWildcardUpdate}
            disabled={loadingWildcard}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={loadingWildcard}
            onClick={() => handleWildcardUpdate(0)}
          >
            Remove
          </Button>
        </Space>
      )}
      <Table
        size="small"
        rowKey={(r) => r.id}
        columns={columns as any}
        dataSource={entries}
        pagination={false}
      />
    </Modal>
  );
};

export default UserCompanyAccessModal;
